// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createActionsHook,
	createStateHook,
} from '@atlassian/react-sweet-state';
import {
	createUpsellAutomationLimitAwarenessLocalStorage,
	type BannerDismissedStates,
	bannerDismissedStatesDefault,
} from '../../services/use-automation-limit-banner/index.tsx';
import { EXPERIMENT_KEY, BANNER_HEIGHT, type Banners } from '../../common/constants.tsx';

type State = {
	isDismissed: boolean;
	height: number;
	isLoading: boolean;
};

export const initialState: State = {
	isDismissed: false,
	height: 0,
	isLoading: false,
};

const getCurrentMonth = () => new Date().getMonth();

export const actions = {
	dismissBanner:
		(cloudId: string, projectType: string, bannerType: string): Action<State> =>
		async ({ setState }) => {
			if (
				!cloudId ||
				(bannerType !== '80_90' && bannerType !== '90_100' && bannerType !== 'gte100')
			) {
				return;
			}

			try {
				const localStorageProvider = createUpsellAutomationLimitAwarenessLocalStorage(cloudId);
				const currentData = localStorageProvider.get(EXPERIMENT_KEY) || {
					...bannerDismissedStatesDefault,
				};

				currentData[projectType][bannerType] = true;
				localStorageProvider.set(EXPERIMENT_KEY, currentData);
				setState({
					isDismissed: true,
					height: 0,
				});
			} catch (error) {
				setState({
					isDismissed: true,
					height: 0,
				});
			}
		},

	checkDismissalState:
		(cloudId: string, projectType: string, bannerType: Banners | undefined): Action<State> =>
		async ({ setState }) => {
			setState({
				isLoading: true,
			});

			if (
				!cloudId ||
				!bannerType ||
				(projectType !== 'software' && projectType !== 'service_desk')
			) {
				setState({
					isDismissed: true,
					isLoading: false,
					height: 0,
				});
				return;
			}
			try {
				const localStorageProvider = createUpsellAutomationLimitAwarenessLocalStorage(cloudId);
				const localStorageData = localStorageProvider.get(EXPERIMENT_KEY);

				let productBannerDismissedStates: BannerDismissedStates;

				// reset state if it doesn't exist or it's expired
				if (!localStorageData || localStorageData[projectType]?.month !== getCurrentMonth()) {
					productBannerDismissedStates = bannerDismissedStatesDefault[projectType];
					localStorageProvider.set(EXPERIMENT_KEY, {
						...bannerDismissedStatesDefault,
					});
				} else {
					productBannerDismissedStates = localStorageData[projectType];
				}

				const isDismissed = productBannerDismissedStates[bannerType] === true;
				setState({
					isDismissed,
					isLoading: false,
					height: isDismissed ? 0 : BANNER_HEIGHT,
				});
			} catch (error) {
				setState({
					isDismissed: false,
					isLoading: false,
					height: 0,
				});
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'AutomationLimitAwarenessBanner',
	initialState,
	actions,
});

export const useAutomationLimitBannerState = createStateHook(Store);
export const useAutomationLimitBannerActions = createActionsHook(Store);
